import { useEffect, useRef } from "react";
import { useSettings } from "../context/SettingsContext";
import { getAtmosphereManifest, atmosphereTrackUrl } from "../api";
import { pickTrack } from "./atmosphere";
import { track as trackEvent } from "./analytics";

// shared manifest cache across mounts
let _manifest = null;
let _manifestPromise = null;
function loadManifest() {
  if (_manifest) return Promise.resolve(_manifest);
  if (!_manifestPromise) {
    _manifestPromise = getAtmosphereManifest()
      .then((t) => { _manifest = t; return t; })
      .catch(() => { _manifest = []; return []; });
  }
  return _manifestPromise;
}

const BASE = { ambient: 0.32, nature: 0.5, ancient: 0.3, hymns: 0.34 };
const DUCK = 0.16;       // multiplier when narration is playing (well under the voice)
const HOLD_MS = 12000;   // let music linger into the reflection

// Background reading atmosphere: fades in on chapter open, loops, ducks under
// narration, and lingers ~12s into the chapter-complete reflection before fading.
export function useAtmosphere({ book, chapter, narrating, completed }) {
  const { atmosphere } = useSettings(); // "off" | "ambient" | "nature"
  const audioRef = useRef(null);
  const targetRef = useRef(0);
  const baseRef = useRef(0);
  const curIdRef = useRef(null);
  const suppressRef = useRef(false);
  const wantRef = useRef(false);
  const fadeRef = useRef(null);

  // create the audio element once
  useEffect(() => {
    const a = new Audio();
    a.loop = true;
    a.preload = "auto";
    a.volume = 0;
    audioRef.current = a;
    return () => {
      if (fadeRef.current) clearInterval(fadeRef.current);
      try { a.pause(); a.src = ""; } catch (_) {}
    };
  }, []);

  // Fade toward a target volume over a FIXED time window, then stop. The timer
  // always clears after the window — even on iOS, where `audio.volume` is
  // read-only so a convergence loop would otherwise spin forever and heat the
  // device. When idle there are zero timers running.
  function setTarget(v) {
    targetRef.current = v;
    const el = audioRef.current;
    if (!el) return;
    if (v > 0 && wantRef.current && el.paused) el.play().catch(() => unlockOnGesture());
    if (fadeRef.current) clearInterval(fadeRef.current);
    const from = el.volume;
    const start = (typeof performance !== "undefined" ? performance.now() : Date.now());
    const DUR = 2200;
    fadeRef.current = setInterval(() => {
      const a = audioRef.current;
      if (!a) { clearInterval(fadeRef.current); fadeRef.current = null; return; }
      const now = (typeof performance !== "undefined" ? performance.now() : Date.now());
      const t = Math.min(1, (now - start) / DUR);
      const eased = t * (2 - t); // easeOutQuad
      try { a.volume = Math.min(1, Math.max(0, from + (targetRef.current - from) * eased)); } catch (_) {}
      if (targetRef.current > 0 && wantRef.current && a.paused) a.play().catch(() => unlockOnGesture());
      if (t >= 1) {                                  // window elapsed — always stop
        try { a.volume = Math.min(1, Math.max(0, targetRef.current)); } catch (_) {}
        if (targetRef.current === 0 && !a.paused) a.pause();
        clearInterval(fadeRef.current);
        fadeRef.current = null;
      }
    }, 50);
  }

  // resume audio after a user gesture if autoplay was blocked
  function unlockOnGesture() {
    const handler = () => {
      const el = audioRef.current;
      if (el && targetRef.current > 0 && wantRef.current) el.play().catch(() => {});
      window.removeEventListener("pointerdown", handler);
      window.removeEventListener("touchstart", handler);
      window.removeEventListener("keydown", handler);
    };
    window.addEventListener("pointerdown", handler, { once: true });
    window.addEventListener("touchstart", handler, { once: true });
    window.addEventListener("keydown", handler, { once: true });
  }

  // pick + start the right track when chapter / mode changes
  useEffect(() => {
    suppressRef.current = false;
    const a = audioRef.current;
    if (!a) return;
    if (atmosphere === "off" || !book || !chapter) {
      wantRef.current = false;
      setTarget(0);
      return;
    }
    let active = true;
    loadManifest().then((tracks) => {
      if (!active) return;
      const id = pickTrack(tracks, atmosphere, book, chapter);
      if (!id) { wantRef.current = false; setTarget(0); return; }
      baseRef.current = BASE[atmosphere] || 0.35;
      if (curIdRef.current !== id) {
        curIdRef.current = id;
        a.volume = 0;            // always fade in from silence on a new track
        a.src = atmosphereTrackUrl(id);
        a.load();
        trackEvent("atmosphere_play", { mode: atmosphere, track: id });
      }
      wantRef.current = true;
      a.play().catch(() => unlockOnGesture());
      setTarget(baseRef.current * (narrating ? DUCK : 1));
    });
    return () => { active = false; };
    // eslint-disable-next-line
  }, [atmosphere, book, chapter]);

  // duck under narration
  useEffect(() => {
    if (!wantRef.current || suppressRef.current) return;
    setTarget(baseRef.current * (narrating ? DUCK : 1));
  }, [narrating]);

  // linger into the reflection, then fade out for the moment of stillness
  useEffect(() => {
    if (!completed || atmosphere === "off" || !wantRef.current) return;
    const t = setTimeout(() => {
      suppressRef.current = true;
      setTarget(0);
    }, HOLD_MS);
    return () => clearTimeout(t);
  }, [completed, atmosphere]);
}
